/**
 * Utility functions for order form validation and preparation
 */

/**
 * Validates order amount against min and max constraints
 * @param {string} amount - The input amount
 * @param {number} minQuantity - Minimum allowed quantity
 * @param {number} maxQuantity - Maximum allowed quantity
 * @returns {string|null} Error message or null if valid
 */
export const validateOrderAmount = (amount, minQuantity, maxQuantity) => {
  // Check if empty or not a number
  if (!amount || amount.trim() === '') {
    return 'Please enter an amount';
  }
  
  const numericValue = parseFloat(amount);
  
  // Check if valid number
  if (isNaN(numericValue)) {
    return 'Please enter a valid number';
  }
  
  // Check for minimum amount
  if (minQuantity !== null && numericValue < minQuantity) {
    return `Minimum order size is ${minQuantity}`;
  }
  
  // Check for maximum amount
  if (maxQuantity !== null && numericValue > maxQuantity) {
    return `Maximum order size is ${maxQuantity}`;
  }
  
  return null; // Valid amount
};

/**
 * Validates price entry
 * @param {string} price - The input price
 * @param {number} minPrice - Minimum allowed price 
 * @param {number} maxPrice - Maximum allowed price
 * @returns {string|null} Error message or null if valid
 */
export const validatePrice = (price, minPrice, maxPrice) => {
  if (!price || price.trim() === '') {
    return 'Please enter a price';
  }
  
  const numericValue = parseFloat(price);
  
  if (isNaN(numericValue) || numericValue <= 0) {
    return 'Please enter a valid price';
  }
  
  if (minPrice !== null && numericValue < minPrice) {
    return `Minimum price is ${minPrice}`;
  }
  
  if (maxPrice !== null && numericValue > maxPrice) {
    return `Maximum price is ${maxPrice}`;
  }
  
  return null; // Valid price
};

/**
 * Prepares order details for confirmation
 * @param {object} params - Order parameters
 * @returns {object} Formatted order details for confirmation
 */
export const prepareOrderDetails = ({
  market,
  orderType,
  side,
  limitPrice,
  amount,
  leverage,
  stopLossEnabled,
  stopLossPrice,
  takeProfitEnabled,
  takeProfitPrice,
  bid,
  ask,
  formatCurrency
}) => {
  // Get the correct symbol format required for orders
  const orderSymbol = market.originalId || 
                     market.instrumentId || 
                     market.id || 
                     (market.code ? market.code : null);
  
  // Use the appropriate price based on order direction 
  const orderPrice = side === 'buy' 
    ? (orderType === 'market' ? ask : parseFloat(limitPrice))
    : (orderType === 'market' ? bid : parseFloat(limitPrice));
  
  // Prepare order details
  return {
    symbol: orderSymbol,
    marketName: market.name || orderSymbol,
    side, // 'buy' or 'sell'
    orderType, // 'market' or 'limit'
    price: orderPrice,
    formattedPrice: formatCurrency(orderPrice),
    amount: amount,
    leverage: parseInt(leverage, 10),
    stopLoss: stopLossEnabled ? parseFloat(stopLossPrice) : null,
    takeProfit: takeProfitEnabled ? parseFloat(takeProfitPrice) : null,
    stopLossEnabled,
    takeProfitEnabled
  };
};

/**
 * Formats order parameters for API submission
 * @param {object} orderDetails - The confirmed order details
 * @param {number} contractSize - The contract size
 * @returns {object} Formatted order parameters for API
 */
export const formatOrderParams = (orderDetails, contractSize) => {
  const params = {
    symbol: orderDetails.symbol,
    side: orderDetails.side,
    orderType: orderDetails.orderType,
    price: orderDetails.price,
    quantity: parseFloat(orderDetails.amount),
    leverage: orderDetails.leverage,
    mode: "open" // Default mode is to open a new position
  };
  
  // Add optional parameters if defined
  if (orderDetails.stopLossEnabled && orderDetails.stopLoss) {
    params.stopLoss = orderDetails.stopLoss;
  }
  
  if (orderDetails.takeProfitEnabled && orderDetails.takeProfit) {
    params.takeProfit = orderDetails.takeProfit;
  }
  
  if (contractSize) {
    params.contractSize = contractSize;
  }
  
  return params;
}; 